import { ATTENTION_LIMITS } from '@exante/gyro-core/attention';
import { parseAttentionReadReceipt, parseAttentionSnapshot } from './model.js';
import { AttentionProtocolError } from './model.js';
export function attentionApi(client, petsAvailable) {
    return {
        snapshot: async (signal) => {
            const first = parseAttentionSnapshot(await client.attentionRequest('/v1/attention', { signal }), petsAvailable);
            if (!first.pagination)
                return first;
            if (first.pagination.offset !== 0)
                throw new AttentionProtocolError();
            const items = [...first.items], cursors = new Set();
            let cursor = first.pagination.nextCursor;
            for (let page = 1; cursor !== null; page++) {
                signal?.throwIfAborted();
                if (page >= ATTENTION_LIMITS.pages || cursors.has(cursor))
                    throw new AttentionProtocolError();
                cursors.add(cursor);
                const next = parseAttentionSnapshot(await client.attentionRequest(`/v1/attention?cursor=${encodeURIComponent(cursor)}`, { signal }), petsAvailable);
                signal?.throwIfAborted();
                if (next.revision !== first.revision || !next.pagination || next.pagination.offset !== items.length
                    || next.pagination.totalItems !== first.pagination.totalItems || JSON.stringify(next.counts) !== JSON.stringify(first.counts))
                    throw new AttentionProtocolError();
                items.push(...next.items);
                cursor = next.pagination.nextCursor;
            }
            if (items.length !== first.pagination.totalItems)
                throw new AttentionProtocolError();
            return parseAttentionSnapshot({ enabled: true, items, counts: first.counts, revision: first.revision, asOf: first.asOf,
                ...(first.capabilities ? { capabilities: first.capabilities } : {}) }, petsAvailable, true);
        },
        read: async (input, signal) => parseAttentionReadReceipt(await client.attentionRequest('/v1/attention/read', {
            method: 'POST', body: input, operationId: input.clientRequestId, signal,
        }), input.clientRequestId, input.read, input.items.length),
        readReceipt: async (requestId, signal) => {
            if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(requestId))
                throw new Error('Invalid notification request identity.');
            const result = await client.attentionRequest(`/v1/attention/read-receipts/${requestId}`, { signal });
            return parseAttentionReadReceipt(result?.receipt, requestId);
        },
    };
}
export function gyroAttentionAnswerDriver(client) {
    const uuid = (value) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
    const check = (result, questionId, requestId, conversationId, lookup) => {
        const receipt = result?.receipt;
        if (receipt?.state !== 'applied' || receipt.questionId !== questionId || receipt.clientRequestId !== requestId
            || !['none', 'queued', 'waiting'].includes(receipt.continuation) || !Number.isFinite(Date.parse(receipt.at)))
            throw new AttentionProtocolError();
        if (!lookup && result.continuation && (result.continuation.conversationId !== conversationId || typeof result.continuation.reply !== 'string'
            || result.continuation.reply.length > 12000 || /[\u0000\uD800-\uDFFF]/u.test(result.continuation.reply)))
            throw new AttentionProtocolError();
        return { receipt, ...(!lookup && result.continuation ? { continuation: result.continuation } : {}) };
    };
    return {
        async answer(question, input, signal) {
            if (question.authority.kind !== 'gyro-clarification' || !uuid(question.id) || !uuid(input.clientRequestId))
                throw new AttentionProtocolError();
            const result = await client.attentionRequest(`/v1/attention/gyro-questions/${question.id}/answer`, { method: 'POST', body: input, operationId: input.clientRequestId, signal });
            return check(result, question.id, input.clientRequestId, question.authority.conversationId, false);
        },
        async receipt(question, clientRequestId, signal) {
            if (question.authority.kind !== 'gyro-clarification' || !uuid(question.id) || !uuid(clientRequestId))
                throw new AttentionProtocolError();
            const result = await client.attentionRequest(`/v1/attention/gyro-questions/${question.id}/answer-receipts/${clientRequestId}`, { signal });
            return check(result, question.id, clientRequestId, question.authority.conversationId, true);
        },
    };
}
//# sourceMappingURL=client.js.map